import { create } from 'zustand';
import { LIMITS, type MessageDTO, type MessageTargetRef } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { messageApi } from '@/api/endpoints';
import { useUsers } from './users';

export type ClientMessage = MessageDTO & {
  /** Optimistic message waiting for the server. */
  pending?: boolean;
  /** Optimistic message the server rejected. */
  failed?: string;
  /** Files for retrying a failed send. */
  localFiles?: File[];
};

export interface TargetMessages {
  messages: ClientMessage[];
  status: 'idle' | 'loading' | 'loaded' | 'error';
  error?: string;
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  loadingBefore: boolean;
  loadingAfter: boolean;
}

const MAX_KEPT = 400;

export const targetKey = (t: MessageTargetRef) => (t.channelId ? `c:${t.channelId}` : `d:${t.conversationId}`);
export const keyOfMessage = (m: Pick<MessageDTO, 'channelId' | 'conversationId'>) => targetKey(m);

const emptyTarget: TargetMessages = {
  messages: [],
  status: 'idle',
  hasMoreBefore: false,
  hasMoreAfter: false,
  loadingBefore: false,
  loadingAfter: false,
};

const compare = (a: MessageDTO, b: MessageDTO) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id);

function mergeUnique(existing: ClientMessage[], incoming: ClientMessage[]): ClientMessage[] {
  const map = new Map(existing.map((m) => [m.id, m]));
  for (const m of incoming) map.set(m.id, m);
  return [...map.values()].sort(compare);
}

interface MessagesState {
  byTarget: Record<string, TargetMessages>;
  loadLatest: (target: MessageTargetRef) => Promise<void>;
  loadBefore: (target: MessageTargetRef) => Promise<void>;
  loadAfter: (target: MessageTargetRef) => Promise<void>;
  loadAround: (target: MessageTargetRef, messageId: string) => Promise<void>;
  receive: (message: MessageDTO) => void;
  update: (message: MessageDTO) => void;
  addPending: (message: ClientMessage) => void;
  failPending: (key: string, id: string, error: string) => void;
  removeLocal: (key: string, id: string) => void;
  remove: (key: string, id: string) => void;
  reaction: (key: string, messageId: string, emoji: string, userId: string, add: boolean) => void;
  invalidateAll: () => void;
  /** Drops a conversation's loaded history (you left it or lost access). */
  forget: (key: string) => void;
  reset: () => void;
}

export const useMessages = create<MessagesState>((set, get) => {
  const patch = (key: string, fn: (t: TargetMessages) => Partial<TargetMessages>) =>
    set((s) => {
      const current = s.byTarget[key] ?? emptyTarget;
      return { byTarget: { ...s.byTarget, [key]: { ...current, ...fn(current) } } };
    });

  const cacheAuthors = (messages: MessageDTO[]) =>
    useUsers.getState().upsert(messages.flatMap((m) => [m.author, m.replyTo?.author ?? null]));

  return {
    byTarget: {},

    loadLatest: async (target) => {
      const key = targetKey(target);
      patch(key, () => ({ status: 'loading', error: undefined }));
      try {
        const page = await messageApi.list(target, { limit: LIMITS.messagesPageSize });
        cacheAuthors(page.messages);
        patch(key, (t) => ({
          status: 'loaded',
          // Keep optimistic messages that are still in flight.
          messages: mergeUnique(page.messages, t.messages.filter((m) => m.pending || m.failed)),
          hasMoreBefore: page.hasMoreBefore,
          hasMoreAfter: false,
        }));
      } catch (err) {
        patch(key, () => ({ status: 'error', error: errorMessage(err, 'Could not load messages.') }));
      }
    },

    loadBefore: async (target) => {
      const key = targetKey(target);
      const t = get().byTarget[key];
      const oldest = t?.messages.find((m) => !m.pending && !m.failed);
      if (!t || t.loadingBefore || !t.hasMoreBefore || !oldest) return;
      patch(key, () => ({ loadingBefore: true }));
      try {
        const page = await messageApi.list(target, { before: oldest.id, limit: LIMITS.messagesPageSize });
        cacheAuthors(page.messages);
        patch(key, (cur) => ({ messages: mergeUnique(cur.messages, page.messages), hasMoreBefore: page.hasMoreBefore, loadingBefore: false }));
      } catch {
        patch(key, () => ({ loadingBefore: false }));
      }
    },

    loadAfter: async (target) => {
      const key = targetKey(target);
      const t = get().byTarget[key];
      const newest = [...(t?.messages ?? [])].reverse().find((m) => !m.pending && !m.failed);
      if (!t || t.loadingAfter || !t.hasMoreAfter || !newest) return;
      patch(key, () => ({ loadingAfter: true }));
      try {
        const page = await messageApi.list(target, { after: newest.id, limit: LIMITS.messagesPageSize });
        cacheAuthors(page.messages);
        patch(key, (cur) => {
          let messages = mergeUnique(cur.messages, page.messages);
          let hasMoreBefore = cur.hasMoreBefore;
          if (messages.length > MAX_KEPT) {
            messages = messages.slice(messages.length - MAX_KEPT);
            hasMoreBefore = true;
          }
          return { messages, hasMoreAfter: page.hasMoreAfter, loadingAfter: false, hasMoreBefore };
        });
      } catch {
        patch(key, () => ({ loadingAfter: false }));
      }
    },

    loadAround: async (target, messageId) => {
      const key = targetKey(target);
      patch(key, () => ({ status: 'loading' }));
      try {
        const page = await messageApi.list(target, { around: messageId, limit: LIMITS.messagesPageSize });
        cacheAuthors(page.messages);
        patch(key, () => ({ status: 'loaded', messages: page.messages, hasMoreBefore: page.hasMoreBefore, hasMoreAfter: page.hasMoreAfter }));
      } catch (err) {
        patch(key, () => ({ status: 'error', error: errorMessage(err, 'Could not load that message.') }));
      }
    },

    receive: (message) => {
      const key = keyOfMessage(message);
      const t = get().byTarget[key];
      cacheAuthors([message]);
      if (!t || t.status !== 'loaded') return;
      patch(key, (cur) => {
        const existing = cur.messages.findIndex((m) => m.id === message.id || (message.nonce && m.pending && m.nonce === message.nonce));
        if (existing >= 0) {
          const messages = [...cur.messages];
          messages[existing] = { ...message };
          return { messages: messages.sort(compare) };
        }
        // Viewing an older window: the live tail isn't loaded, so don't append.
        if (cur.hasMoreAfter) return {};
        let messages = [...cur.messages, message].sort(compare);
        let hasMoreBefore = cur.hasMoreBefore;
        if (messages.length > MAX_KEPT) {
          messages = messages.slice(messages.length - MAX_KEPT);
          hasMoreBefore = true;
        }
        return { messages, hasMoreBefore };
      });
    },

    update: (message) => {
      const key = keyOfMessage(message);
      if (!get().byTarget[key]) return;
      cacheAuthors([message]);
      // Only replace — an edit to a message outside the loaded window must not be inserted.
      patch(key, (t) => ({ messages: t.messages.map((m) => (m.id === message.id ? { ...message } : m)) }));
    },

    addPending: (message) => patch(keyOfMessage(message), (t) => ({ messages: [...t.messages, message] })),

    failPending: (key, id, error) =>
      patch(key, (t) => ({ messages: t.messages.map((m) => (m.id === id ? { ...m, pending: false, failed: error } : m)) })),

    removeLocal: (key, id) => patch(key, (t) => ({ messages: t.messages.filter((m) => m.id !== id) })),

    remove: (key, id) =>
      patch(key, (t) => ({
        messages: t.messages
          .filter((m) => m.id !== id)
          // Replies to a deleted message lose their preview, like on the server.
          .map((m) => (m.replyToId === id ? { ...m, replyTo: null } : m)),
      })),

    reaction: (key, messageId, emoji, userId, add) =>
      patch(key, (t) => ({
        messages: t.messages.map((m) => {
          if (m.id !== messageId) return m;
          const reactions = m.reactions.map((r) => ({ ...r, userIds: [...r.userIds] }));
          const entry = reactions.find((r) => r.emoji === emoji);
          if (add) {
            if (entry?.userIds.includes(userId)) return m;
            if (entry) {
              entry.userIds.push(userId);
              entry.count = entry.userIds.length;
            } else reactions.push({ emoji, count: 1, userIds: [userId] });
          } else {
            if (!entry || !entry.userIds.includes(userId)) return m;
            entry.userIds = entry.userIds.filter((id) => id !== userId);
            entry.count = entry.userIds.length;
          }
          return { ...m, reactions: reactions.filter((r) => r.count > 0) };
        }),
      })),

    invalidateAll: () =>
      set((s) => ({
        byTarget: Object.fromEntries(Object.entries(s.byTarget).map(([k, t]) => [k, { ...t, status: 'idle' as const }])),
      })),

    forget: (key) =>
      set((s) => {
        const { [key]: _forgotten, ...byTarget } = s.byTarget;
        return { byTarget };
      }),

    reset: () => set({ byTarget: {} }),
  };
});

export function useTargetMessages(key: string): TargetMessages {
  return useMessages((s) => s.byTarget[key] ?? emptyTarget);
}
